import React, { useState, useRef, useEffect } from 'react';
import { useJarvisContext, A } from '../../context/JarvisContext';
import { describeVoiceError } from '../../services/voiceService';

const BAR_STYLES = `
@keyframes orbPulse {
  0%,100% { box-shadow:0 0 14px #00d4ff, 0 0 28px #00d4ff55, inset 0 0 10px #00d4ff22; }
  50%      { box-shadow:0 0 28px #00d4ff, 0 0 56px #00d4ffaa, inset 0 0 24px #00d4ff44; }
}
@keyframes orbRing {
  0%   { transform:scale(1);   opacity:0.6; }
  100% { transform:scale(1.8); opacity:0; }
}
@keyframes micPulse {
  0%,100% { box-shadow:0 0 0 0 rgba(0,212,255,0.4); }
  50%     { box-shadow:0 0 0 8px rgba(0,212,255,0); }
}
@keyframes sendPop {
  0%   { transform:scale(1); }
  50%  { transform:scale(0.88); }
  100% { transform:scale(1); }
}
@media (max-width: 480px) {
  .v5-bottombar { padding: 10px 8px 12px !important; }
}
`;

// ── JARVIS orb with animated rings ───────────────────────────────────────────
function JarvisOrb({ onClick, listening }) {
  return (
    <div onClick={onClick} style={{ position: 'relative', width: '46px', height: '46px', flexShrink: 0, cursor: 'pointer' }}>
      {/* Outer expanding ring */}
      <div style={{
        position: 'absolute', inset: '-6px', borderRadius: '50%',
        border: '1px solid rgba(0,212,255,0.3)',
        animation: 'orbRing 2s ease-out infinite',
        pointerEvents: 'none',
      }}/>
      <div style={{
        position: 'absolute', inset: '-3px', borderRadius: '50%',
        border: '1px solid rgba(0,212,255,0.2)',
        animation: 'orbRing 2s ease-out infinite 0.7s',
        pointerEvents: 'none',
      }}/>

      {/* Main orb */}
      <div style={{
        width: '46px', height: '46px', borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 35%, #00d4ff, #0044cc 60%, #001b55)',
        animation: 'orbPulse 3s ease-in-out infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '18px', fontWeight: '900', color: '#fff', fontFamily: 'monospace',
        letterSpacing: '1px', userSelect: 'none',
      }}>
        J
      </div>

      {/* Listening indicator */}
      {listening && (
        <div style={{
          position: 'absolute', bottom: '-1px', right: '-1px',
          width: '12px', height: '12px', borderRadius: '50%',
          background: '#ef4444', border: '2px solid #0a0f1e',
          animation: 'micPulse 1s ease-in-out infinite',
        }}/>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function BottomBar() {
  const { dispatch } = useJarvisContext();
  const [text,      setText]      = useState('');
  const [listening, setListening] = useState(false);
  const [hover,     setHover]     = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const inputRef  = useRef(null);
  const recogRef  = useRef(null);

  // ── Web Speech API ──────────────────────────────────────────────────────────
  const startVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceError('Ha browser voice input support करत नाही. Chrome किंवा Edge वापरा.');
      return;
    }
    if (listening) {
      recogRef.current?.stop();
      setListening(false);
      return;
    }
    setVoiceError('');
    const recog = new SpeechRecognition();
    recog.lang = 'en-IN';
    recog.continuous = false;
    recog.interimResults = false;
    recog.onstart  = () => setListening(true);
    recog.onend    = () => setListening(false);
    // Previously silent (just reset listening state) — now surfaces the
    // actual reason (permission denied, no mic found, etc.) instead of
    // the button just quietly stopping with no explanation.
    recog.onerror  = (e) => {
      setListening(false);
      const msg = describeVoiceError(e.error);
      if (msg) setVoiceError(msg);
    };
    recog.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setText(prev => (prev ? prev + ' ' + transcript : transcript));
      inputRef.current?.focus();
    };
    recog.start();
    recogRef.current = recog;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { recogRef.current?.abort(); };
  }, []);

  // ── Submit message ──────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e?.preventDefault();
    const msg = text.trim();
    if (!msg) return;
    // Navigate to chat screen and pass the message via localStorage
    localStorage.setItem('jarvis_pending_message', msg);
    setText('');
    dispatch({ type: A.SET_SCREEN, payload: 'v5chat' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const lowerMsg = text.toLowerCase();
  const isAnalysis = lowerMsg.includes('analyz') || lowerMsg.includes('gold') ||
                     lowerMsg.includes('xauusd') || lowerMsg.includes('trade') ||
                     lowerMsg.includes('buy') || lowerMsg.includes('sell');
  const sendColor = text.trim() ? (isAnalysis ? '#10b981' : '#00d4ff') : 'rgba(255,255,255,0.2)';

  return (
    <>
      <style>{BAR_STYLES}</style>

      <div className="v5-bottombar" style={{
        position:   'fixed',
        bottom:     0, left: 0, right: 0,
        zIndex:     1000,
        padding:    '12px 20px 16px',
        background: 'linear-gradient(to top, rgba(5,8,18,0.98) 70%, transparent)',
        backdropFilter: 'blur(16px)',
      }}>
        {voiceError && (
          <div style={{
            maxWidth: '860px', margin: '0 auto 8px', padding: '8px 12px', borderRadius: '8px',
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#f87171', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          }}>
            <span>🎙️ {voiceError}</span>
            <button onClick={() => setVoiceError('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
          </div>
        )}
        <form onSubmit={handleSubmit} style={{
          display:      'flex',
          alignItems:   'center',
          gap:          '12px',
          background:   hover ? 'rgba(0,212,255,0.06)' : 'rgba(255,255,255,0.04)',
          border:       `1px solid ${hover ? 'rgba(0,212,255,0.25)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: '28px',
          padding:      '8px 12px 8px 14px',
          transition:   'all 0.25s',
          maxWidth:     '860px',
          margin:       '0 auto',
        }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}>

          {/* JARVIS orb — clicking navigates to home */}
          <JarvisOrb
            onClick={() => dispatch({ type: A.SET_SCREEN, payload: 'v5home' })}
            listening={listening}
          />

          {/* Text input */}
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={listening ? '🎤 Listening...' : 'Ask JARVIS anything...'}
            style={{
              flex:        1,
              background:  'transparent',
              border:      'none',
              outline:     'none',
              color:       listening ? '#f87171' : '#e2e8f0',
              fontSize:    '14px',
              fontFamily:  'inherit',
              letterSpacing: '0.2px',
            }}
          />

          {/* Mic button */}
          <button
            type="button"
            onClick={startVoice}
            title={listening ? 'Stop listening' : 'Voice input'}
            style={{
              background:   listening ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)',
              border:       `1px solid ${listening ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '50%',
              width:        '36px', height: '36px',
              display:      'flex', alignItems: 'center', justifyContent: 'center',
              cursor:       'pointer',
              fontSize:     '16px',
              flexShrink:   0,
              animation:    listening ? 'micPulse 1s ease-in-out infinite' : 'none',
            }}>
            {listening ? '⏹' : '🎤'}
          </button>

          {/* Send button */}
          {text.trim() && (
            <button
              type="submit"
              style={{
                background:   `linear-gradient(135deg, ${sendColor === '#10b981' ? '#10b981, #059669' : '#00d4ff, #0066cc'})`,
                border:       'none',
                borderRadius: '50%',
                width:        '36px', height: '36px',
                display:      'flex', alignItems: 'center', justifyContent: 'center',
                cursor:       'pointer',
                fontSize:     '16px',
                flexShrink:   0,
                boxShadow:    `0 0 12px ${sendColor}66`,
              }}>
              ➤
            </button>
          )}
        </form>

        {/* Hint text */}
        {!text && (
          <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '10px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>
            Say "Analyze Gold" · "Today's setup" · "What is FVG?" · or anything
          </div>
        )}
      </div>
    </>
  );
}
